import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import PrivacyNotice from "@/components/PrivacyNotice";
import { api } from "@/lib/api";
import {
  Search, Users, Building2, UserPlus, TrendingUp, Layers,
  FileText, DollarSign,
} from "lucide-react";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Customers() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("recent");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/customers", { params: { q, customer_type: type, status, sort } }),
        api.get("/customers/summary"),
      ]);
      setRows(list || []);
      setSummary(sum);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, type, status, sort]);

  const cards = useMemo(() => summary ? [
    { icon: Users, label: "Total Customers", value: summary.total_customers, tint: "emerald" },
    { icon: UserPlus, label: "New This Month", value: summary.new_this_month, tint: "amber" },
    { icon: Building2, label: "Corporate", value: summary.corporate_customers, tint: "blue" },
    { icon: TrendingUp, label: "Individual", value: summary.individual_customers, tint: "slate" },
    { icon: Layers, label: "Total Bookings", value: summary.total_bookings, tint: "purple" },
    { icon: DollarSign, label: "Business Generated", value: rupees(summary.total_business), tint: "emerald", isMoney: true },
  ] : [], [summary]);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar />
      <main className="flex-1">
        <header className="bg-white border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="overline text-[10px] tracking-widest text-slate-500">CUSTOMER MANAGEMENT</div>
              <h1 className="font-editorial text-3xl mt-1">All Customers</h1>
            </div>
            <button
              data-testid="new-customer-btn"
              onClick={() => nav("/customers/new")}
              className="btn-bronze inline-flex items-center gap-2 text-sm"
            >
              <UserPlus size={15} /> Add New Customer
            </button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {cards.map(({ icon: Icon, label, value, tint, isMoney }) => (
              <div key={label} className="bg-white border border-border rounded p-4">
                <div className={`w-8 h-8 rounded flex items-center justify-center mb-3 bg-${tint}-50 text-${tint}-600`}>
                  <Icon size={16} />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
                <div className={`mt-1 font-editorial text-slate-900 ${isMoney ? "text-lg" : "text-2xl"}`}>
                  {value ?? "—"}
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white border border-border rounded p-4 mb-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="customer-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, mobile, email, company, customer ID…"
                className="w-full pl-9 pr-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              />
            </div>
            <select data-testid="filter-type" value={type} onChange={(e) => setType(e.target.value)}
                    className="border border-border rounded px-3 py-2 text-sm bg-white">
              <option value="">All types</option>
              <option value="individual">Individual</option>
              <option value="corporate">Corporate</option>
            </select>
            <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
                    className="border border-border rounded px-3 py-2 text-sm bg-white">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)}
                    className="border border-border rounded px-3 py-2 text-sm bg-white">
              <option value="recent">Latest activity</option>
              <option value="name">Name A–Z</option>
              <option value="created">Newest created</option>
            </select>
          </div>

          {/* List */}
          <div className="bg-white border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Customer ID</th>
                  <th className="text-left px-6 py-3">Name</th>
                  <th className="text-left px-6 py-3">Type</th>
                  <th className="text-left px-6 py-3">Contact</th>
                  <th className="text-left px-6 py-3">City</th>
                  <th className="text-left px-6 py-3">Last activity</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={7} className="px-6 py-8 text-center text-slate-400">Loading…</td></tr>)}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">
                    No customers yet. Click <b>Add New Customer</b> to get started.
                  </td></tr>
                )}
                {!loading && rows.map((c) => {
                  const nm = c.customer_type === "corporate" && c.company_name
                    ? c.company_name
                    : [c.title, c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
                  return (
                    <tr key={c.id} data-testid={`customer-row-${c.id}`}
                        onClick={() => nav(`/customers/${c.id}`)}
                        className="border-t border-border cursor-pointer hover:bg-slate-50">
                      <td className="px-6 py-3 font-mono-e text-xs text-slate-700">{c.customer_id}</td>
                      <td className="px-6 py-3">
                        <div className="font-medium text-slate-900">{nm || "—"}</div>
                        {c.customer_type === "corporate" && c.company_name && (
                          <div className="text-[11px] text-slate-500">{[c.first_name, c.last_name].filter(Boolean).join(" ")}</div>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${c.customer_type === "corporate" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {c.customer_type === "corporate" ? <Building2 size={10} /> : <Users size={10} />}
                          {c.customer_type}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-xs text-slate-600">
                        {c.mobile && <div>{c.mobile}</div>}
                        {c.email && <div className="text-slate-400">{c.email}</div>}
                      </td>
                      <td className="px-6 py-3 text-xs">{[c.city, c.country].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-6 py-3 text-xs text-slate-500">
                        {c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <FileText size={14} className="text-slate-300" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PrivacyNotice />
        </div>
      </main>
    </div>
  );
}
